import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, notification, theme, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/general/layout/page-header";
import SearchFilterCard from "../../../components/general/filter-card";
import ProductsFilter, {
  FilterValues,
} from "../../../components/order/add-products/ProductsFilter";
import ProductGrid from "../../../components/order/add-products/ProductGrid";
import SelectedProductsList, {
  SelectedProduct,
} from "../../../components/order/add-products/SelectedProductsList";
import ProductQuantityModal, {
  QuantityData,
} from "../../../components/order/add-products/ProductQuantityModal";
import { IProduct } from "../../../models/product/response";
import EndPoints from "../../../services/end-points";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import CategoryContext from "../../../context/category/context";
import { ITrademark } from "../../../models/trademark/response";
import StoreSelector from "../../../components/order/product-management/StoreSelector";

interface RouteParams {
  orderId: string;

  [key: string]: string | undefined;
}

const AddProductsToOrderPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<RouteParams>();
  const orderId = Number(params.orderId);

  // Context
  const { allCategories, actions: categoryActions } =
    useContext(CategoryContext);

  // State from location (passed from order details page)
  const locationState =
    (location.state as {
      storeId?: number;
      orderNumber?: number;
      existingProductIds?: number[];
    }) || {};
  const [storeId, setStoreId] = useState<number | undefined>(
    locationState.storeId,
  );
  const [orderNumber] = useState<number | undefined>(locationState.orderNumber);
  const [existingProductIds] = useState<number[]>(
    locationState.existingProductIds || [],
  );

  // Products state
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Trademarks state
  const [trademarks, setTrademarks] = useState<ITrademark[]>([]);

  // Selected products state
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );

  // Modal state
  const [quantityModalVisible, setQuantityModalVisible] = useState(false);
  const [quantityModalProduct, setQuantityModalProduct] =
    useState<IProduct | null>(null);
  const [quantityModalMode, setQuantityModalMode] = useState<"add" | "edit">(
    "add",
  );
  const [editingProductId, setEditingProductId] = useState<number | null>(null);

  // Filters state
  const [filters, setFilters] = useState<FilterValues>({});

  // Services
  const [orderProductService] = useState(
    () => new OrderProductService(httpclient),
  );

  // Load categories and trademarks on mount
  useEffect(() => {
    if (!allCategories) {
      categoryActions.getAllData();
    }
    fetchTrademarks();
  }, []);

  // Fetch products when filters, store, page, or perPage change
  useEffect(() => {
    fetchProducts();
  }, [filters, currentPage, perPage, storeId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [storeId]);

  const fetchTrademarks = async () => {
    try {
      const response = await EndPoints.trademark.getAllTrademark({
        light: true,
        perPage: -1,
      });
      if (response.data?.data) {
        setTrademarks(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching trademarks:", error);
    }
  };

  const fetchProducts = async () => {
    if (!storeId) {
      setProducts([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        perPage: perPage,
      };

      // Add filters
      let filterIndex = 0;

      // Exclude products already in order
      if (existingProductIds.length > 0) {
        params[`filters[${filterIndex}][name]`] = "id";
        params[`filters[${filterIndex}][value]`] = existingProductIds.join(",");
        params[`filters[${filterIndex}][operation]`] = "not-in";
        filterIndex++;
      }

      // Always scope results to selected store
      params[`filters[${filterIndex}][name]`] = "storeId";
      params[`filters[${filterIndex}][value]`] = storeId;
      params[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;

      // Status filter (only active products globally)
      params[`filters[${filterIndex}][name]`] = "status";
      params[`filters[${filterIndex}][value]`] = "1";
      params[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;

      // Store-level status filter (only active in selected store)
      params[`filters[${filterIndex}][name]`] = "storeStatus";
      params[`filters[${filterIndex}][value]`] = "1";
      params[`filters[${filterIndex}][operation]`] = "eq";
      filterIndex++;

      // Category filters
      if (filters.childCategoryId) {
        params[`filters[${filterIndex}][name]`] = "childCategoryId";
        params[`filters[${filterIndex}][value]`] = filters.childCategoryId;
        params[`filters[${filterIndex}][operation]`] = "eq";
        filterIndex++;
      } else if (filters.parentCategoryId) {
        params[`filters[${filterIndex}][name]`] = "parentCategoryId";
        params[`filters[${filterIndex}][value]`] = filters.parentCategoryId;
        params[`filters[${filterIndex}][operation]`] = "eq";
        filterIndex++;
      } else if (filters.mainCategoryId) {
        params[`filters[${filterIndex}][name]`] = "mainCategoryId";
        params[`filters[${filterIndex}][value]`] = filters.mainCategoryId;
        params[`filters[${filterIndex}][operation]`] = "eq";
        filterIndex++;
      }

      // Trademark filter
      if (filters.trademarkId) {
        params[`filters[${filterIndex}][name]`] = "trademarkId";
        params[`filters[${filterIndex}][value]`] = filters.trademarkId;
        params[`filters[${filterIndex}][operation]`] = "eq";
        filterIndex++;
      }

      // Availability filter
      if (filters.available !== null && filters.available !== undefined) {
        params[`filters[${filterIndex}][name]`] = "available";
        params[`filters[${filterIndex}][value]`] = filters.available
          ? "1"
          : "0";
        params[`filters[${filterIndex}][operation]`] = "eq";
        filterIndex++;
      }

      // Search keyword
      if (filters.keyword && filters.keyword.trim() !== "") {
        params.keyword = filters.keyword;
      }

      const response = await EndPoints.product.getAllProducts(params);

      if (response.data) {
        setProducts(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (error: any) {
      console.error("Error fetching products:", error);
      if (!error?.response && !error?.errorType) {
        notification.error({
          message: t("error"),
          description: error?.message || t("failed_to_load_products"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: FilterValues) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    if (pageSize !== perPage) {
      setPerPage(pageSize);
    }
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleProductAdd = (product: IProduct) => {
    setQuantityModalProduct(product);
    setQuantityModalMode("add");
    setEditingProductId(null);
    setQuantityModalVisible(true);
  };

  const handleProductEdit = (product: IProduct) => {
    const selectedProduct = selectedProducts.find(
      (sp) => sp.productId === product.id,
    );
    if (selectedProduct) {
      setQuantityModalProduct(product);
      setQuantityModalMode("edit");
      setEditingProductId(product.id);
      setQuantityModalVisible(true);
    }
  };

  const handleQuantitySubmit = (
    productId: number,
    quantityData: QuantityData,
  ) => {
    const product =
      products.find((p) => p.id === productId) || quantityModalProduct;
    if (!product) return;

    if (quantityModalMode === "add") {
      // Add new product
      setSelectedProducts([
        ...selectedProducts,
        {
          productId: product.id,
          product: product,
          quantityData: quantityData,
        },
      ]);
      notification.success({
        message: t("success"),
        description: t("product_added_to_selection"),
      });
    } else {
      // Edit existing product
      setSelectedProducts(
        selectedProducts.map((sp) =>
          sp.productId === productId
            ? { ...sp, quantityData: quantityData }
            : sp,
        ),
      );
      notification.success({
        message: t("success"),
        description: t("product_quantity_updated"),
      });
    }

    setQuantityModalVisible(false);
    setQuantityModalProduct(null);
  };

  const handleEditSelectedProduct = (selectedProduct: SelectedProduct) => {
    setQuantityModalProduct(selectedProduct.product);
    setQuantityModalMode("edit");
    setEditingProductId(selectedProduct.productId);
    setQuantityModalVisible(true);
  };

  const handleRemoveSelectedProduct = (productId: number) => {
    setSelectedProducts(
      selectedProducts.filter((sp) => sp.productId !== productId),
    );
    notification.success({
      message: t("success"),
      description: t("product_removed_from_selection"),
    });
  };

  const handleSaveAll = async () => {
    if (selectedProducts.length === 0) {
      notification.warning({
        message: t("warning"),
        description: t("please_select_at_least_one_product"),
      });
      return;
    }

    if (!storeId) {
      notification.error({
        message: t("error"),
        description: t("store_id_missing"),
      });
      return;
    }

    setLoading(true);
    try {
      const products = selectedProducts.map((sp) => ({
        product_id: sp.productId,
        count: sp.quantityData.count,
        unit_partition_id: sp.quantityData.unitPartitionId,
        available_weight: sp.quantityData.availableWeight,
      }));

      await orderProductService.addProducts(orderId, {
        storeId: storeId,
        products: products,
      });

      notification.success({
        message: t("success"),
        description: t("products_added_successfully"),
      });

      // Navigate back to order details
      navigate(`/orders/details/${orderId}`);
    } catch (error: any) {
      console.error("Error adding products:", error);

      if (!error?.response && !error?.errorType) {
        notification.error({
          message: t("error"),
          description: error?.message || t("failed_to_add_products"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/orders/details/${orderId}`);
  };

  const getInitialQuantityData = (): QuantityData | undefined => {
    if (quantityModalMode === "edit" && editingProductId) {
      const selectedProduct = selectedProducts.find(
        (sp) => sp.productId === editingProductId,
      );
      return selectedProduct?.quantityData;
    }
    return undefined;
  };

  if (!allCategories) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <Spin size="large" tip={t("loading")} />
      </div>
    );
  }

  const selectedProductIds = selectedProducts.map((sp) => sp.productId);

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title={t("add_products_to_order")}
        subTitle={`${t("number_id")} #${orderNumber}`}
        extra={[
          <Button
            key="cancel"
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            disabled={loading}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveAll}
            loading={loading}
            disabled={selectedProducts.length === 0}
          >
            {t("save_and_add_products")} ({selectedProducts.length})
          </Button>,
        ]}
      />

      {/* Store Selector */}
      <div
        style={{
          padding: "20px",
          backgroundColor: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: 24,
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: token.colorText,
            display: "block",
            marginBottom: 8,
          }}
        >
          {t("store")} <span style={{ color: token.colorError }}>*</span>
        </label>
        <StoreSelector
          value={storeId}
          onChange={setStoreId}
          placeholder={t("select_store_to_view_quantities")}
        />
      </div>

      {/* Selected Products List */}
      <SelectedProductsList
        selectedProducts={selectedProducts}
        onEdit={handleEditSelectedProduct}
        onRemove={handleRemoveSelectedProduct}
        storeId={storeId}
      />

      {/* Filters */}
      <SearchFilterCard
        formId="add-products-filter-form"
        searchValue={filters.keyword}
        setSearchValue={(value) => {
          handleFilterChange({ ...filters, keyword: value });
        }}
        onReset={() => {
          handleFilterChange({});
        }}
        resetLoading={loading}
        applyLoading={loading}
      >
        <ProductsFilter
          categories={allCategories || []}
          trademarks={trademarks}
          initialValues={filters}
          onFilterChange={(newFilters) =>
            handleFilterChange({ ...filters, ...newFilters })
          }
          formId="add-products-filter-form"
        />
      </SearchFilterCard>

      {/* Products Grid */}
      <ProductGrid
        products={products}
        loading={loading}
        storeId={storeId}
        selectedProductIds={selectedProductIds}
        currentPage={currentPage}
        perPage={perPage}
        total={total}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onProductAdd={handleProductAdd}
        onProductEdit={handleProductEdit}
      />

      {/* Quantity Modal */}
      <ProductQuantityModal
        visible={quantityModalVisible}
        product={quantityModalProduct}
        storeId={storeId}
        mode={quantityModalMode}
        initialQuantity={getInitialQuantityData()}
        onCancel={() => {
          setQuantityModalVisible(false);
          setQuantityModalProduct(null);
        }}
        onSubmit={handleQuantitySubmit}
      />
    </div>
  );
};

export default AddProductsToOrderPage;
